import type { Feature, GeoJsonProperties, Geometry } from "geojson";
import type { MapLocation } from "@/Shared/Maps/MapLocation";
import type { LatitudeLongitude } from "@freightsimple/generated-dashboard-openapi-client/models/LatitudeLongitude";
import type { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { calculateBoundingBox } from "@/Helpers/calculateBoundingBox";
import {
  calculatePointOnArc,
  calculatePointsOnArc,
} from "@/Helpers/calculatePointsOnArc";
import { getMarkerName } from "./images";
import { hasValidLatitudeLongitude } from "./hasValidLatitudeLongitude";
import { latitudeLongitudeToNumbers } from "./latitudeLongitudeToNumbers";
import { pathColorForShipmentState } from "./pathColorForShipmentState";

export interface MapLane {
  pickup: Partial<MapLocation>;
  delivery: Partial<MapLocation>;
  shipmentState: ShipmentState;
}

const ARC_SEGMENTS = 10;

// Every source and layer this adds is prefixed per lane, because a map showing
// several shipments adds one set per lane and Mapbox rejects a duplicate id.
function laneIds(laneIndex: number) {
  return {
    pickupSource: `lane${laneIndex}PickupSource`,
    pickupLayer: `lane${laneIndex}PickupPoint`,
    deliverySource: `lane${laneIndex}DeliverySource`,
    deliveryLayer: `lane${laneIndex}DeliveryPoint`,
    arcSource: (segment: number) => `lane${laneIndex}RouteSource${segment}`,
    arcLayer: (segment: number) => `lane${laneIndex}Route${segment}`,
  };
}

function removeLayerAndSource(
  map: mapboxgl.Map,
  layerId: string,
  sourceId: string,
) {
  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }
}

function pointFeature(
  latitudeLongitude: Required<LatitudeLongitude>,
): Feature<Geometry, GeoJsonProperties> {
  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Point",
      coordinates: latitudeLongitudeToNumbers(latitudeLongitude),
    },
  };
}

function addEndpoint(
  map: mapboxgl.Map,
  sourceId: string,
  layerId: string,
  pickupOrDelivery: "pickup" | "delivery",
  latitudeLongitude: Required<LatitudeLongitude>,
  shipmentState: ShipmentState,
) {
  map.addSource(sourceId, {
    type: "geojson",
    data: pointFeature(latitudeLongitude),
  });

  map.addLayer({
    id: layerId,
    source: sourceId,
    type: "symbol",
    layout: {
      "icon-image": getMarkerName(pickupOrDelivery, shipmentState),
      "icon-rotate": ["get", "bearing"],
      "icon-rotation-alignment": "map",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });
}

function addLane(map: mapboxgl.Map, lane: MapLane, laneIndex: number) {
  // Clear this lane's own layers first. Mapbox throws on a duplicate source id,
  // and the caller retries on a thrown "style is not loaded" — so an add that
  // failed part way must not leave anything for the retry to collide with.
  removeLane(map, laneIndex);

  const ids = laneIds(laneIndex);
  const { pickup, delivery, shipmentState } = lane;

  const pickupValid = hasValidLatitudeLongitude(pickup);
  const deliveryValid = hasValidLatitudeLongitude(delivery);

  if (pickupValid) {
    addEndpoint(
      map,
      ids.pickupSource,
      ids.pickupLayer,
      "pickup",
      pickup.latitudeLongitude,
      shipmentState,
    );
  }

  if (deliveryValid) {
    addEndpoint(
      map,
      ids.deliverySource,
      ids.deliveryLayer,
      "delivery",
      delivery.latitudeLongitude,
      shipmentState,
    );
  }

  // Only draw the route when both endpoints are usable — a missing or (0,0)
  // coordinate would otherwise anchor the line in the ocean off Africa
  // (ENG-4342). The valid endpoint still stands alone.
  if (!pickupValid || !deliveryValid) {
    return;
  }

  const pathColor = pathColorForShipmentState(shipmentState);
  const points = calculatePointsOnArc(
    pickup.latitudeLongitude,
    delivery.latitudeLongitude,
    ARC_SEGMENTS,
  ).map((coordinate) => latitudeLongitudeToNumbers(coordinate));

  for (let segment = 0; segment < points.length - 1; segment++) {
    const start = points[segment];
    const end = points[segment + 1];
    if (start === undefined || end === undefined) {
      continue;
    }

    map.addSource(ids.arcSource(segment), {
      type: "geojson",
      data: {
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: [start, end] },
      },
    });

    map.addLayer({
      id: ids.arcLayer(segment),
      source: ids.arcSource(segment),
      type: "line",
      paint: { "line-width": 2, "line-color": pathColor },
    });
  }
}

function removeLane(map: mapboxgl.Map, laneIndex: number) {
  const ids = laneIds(laneIndex);

  removeLayerAndSource(map, ids.pickupLayer, ids.pickupSource);
  removeLayerAndSource(map, ids.deliveryLayer, ids.deliverySource);

  for (let segment = 0; segment < ARC_SEGMENTS; segment++) {
    removeLayerAndSource(map, ids.arcLayer(segment), ids.arcSource(segment));
  }
}

const CAMERA_EASE_MS = 600;

function zoomToLanes(map: mapboxgl.Map, lanes: MapLane[]) {
  const points: Array<[number, number]> = [];

  lanes.forEach((lane) => {
    const { pickup, delivery } = lane;
    const pickupValid = hasValidLatitudeLongitude(pickup);
    const deliveryValid = hasValidLatitudeLongitude(delivery);

    if (pickupValid) {
      points.push(latitudeLongitudeToNumbers(pickup.latitudeLongitude));
    }
    if (deliveryValid) {
      points.push(latitudeLongitudeToNumbers(delivery.latitudeLongitude));
    }
    // The arc bulges past a straight line between its endpoints, so include
    // its midpoint or the camera clips the top of the route.
    if (pickupValid && deliveryValid) {
      points.push(
        latitudeLongitudeToNumbers(
          calculatePointOnArc(
            pickup.latitudeLongitude,
            delivery.latitudeLongitude,
            0.5,
          ),
        ),
      );
    }
  });

  if (points.length === 0) {
    map.easeTo({ center: [-90, 44], zoom: 3, duration: CAMERA_EASE_MS });
    return;
  }

  const firstPoint = points[0];
  if (points.length === 1 && firstPoint !== undefined) {
    map.easeTo({ center: firstPoint, zoom: 5, duration: CAMERA_EASE_MS });
    return;
  }

  try {
    map.fitBounds(calculateBoundingBox(points), {
      padding: 100,
      duration: CAMERA_EASE_MS,
    });
  } catch {
    // A zero-size bounding box (every lane on one point) can't be fitted.
    map.easeTo({ center: [-90, 44], zoom: 3, duration: CAMERA_EASE_MS });
  }
}

/**
 * Frames the lanes given without drawing any of them. Pass the whole group for
 * the resting view, or one lane to zoom in on it.
 */
export function frameLanes(map: mapboxgl.Map, lanes: MapLane[]) {
  zoomToLanes(map, lanes);
}

/**
 * Draws a single lane, clearing whichever one was drawn before. Only one lane
 * shows at a time — the list highlights the card under the cursor.
 */
export function drawOneLane(
  map: mapboxgl.Map,
  lane: MapLane | undefined,
  previouslyDrawnLaneIndex: number | undefined,
  laneIndex: number | undefined,
) {
  if (previouslyDrawnLaneIndex !== undefined) {
    removeLane(map, previouslyDrawnLaneIndex);
  }

  if (lane !== undefined && laneIndex !== undefined) {
    addLane(map, lane, laneIndex);
  }
}
